import { useQuery } from '@tanstack/react-query';

import { listLlmProfiles } from '@/lib/api/generated/api';

import { llmProfileKeys } from '../queryKeys';
import type { LlmProfile } from '../types';

/**
 * Every LLM profile defined on the organization, as a flat list.
 *
 * Kept flat, so the workflow builder's step editor (spec 067 US2,
 * `StepInspector.tsx`), which reads this hook to populate its own profile
 * picker, is unaffected by how the admin page presents the same profiles. That
 * page reads `useLlmProfileRows` instead (spec 136): a different endpoint, a
 * different audience and a wider shape, which is why this hook was not widened
 * to serve both.
 *
 * Anything but a 200 throws rather than resolving to an empty list, so the
 * caller can tell "this organization has none" from "we could not find out."
 * The generated `listLlmProfiles` resolves to `{ items: LLMProfileView[] }`,
 * not a bare array — unwrapped here.
 */
export function useLlmProfiles() {
  const query = useQuery({
    queryKey: llmProfileKeys.list(),
    queryFn: async (): Promise<LlmProfile[]> => {
      const response = await listLlmProfiles();
      if (response.status !== 200) {
        throw new Error('The LLM profiles could not be loaded.');
      }
      return response.data.items;
    },
    retry: false,
  });

  return {
    entries: query.data,
    isPending: query.isPending,
    loadFailed: query.isError,
    refetch: query.refetch,
  };
}
